import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useWikiV2Client, wikiErrorStatus } from '../../../hooks/useWikiV2';
import WikiIcon from '../shared/WikiIcon';
import WikiAccessPanel from '../access/WikiAccessPanel';
import { invalidateWiki, useWikiQuery, wikiKeys } from '../shared/useWikiQueries';
import { dateLabel } from '../shared/wikiFormat';
import { wikiPaths } from '../shared/wikiRoutes';
import { wikiUi } from '../shared/wikiUi';
import {
    PAGE_COPY,
    ackVersion,
    readLabel,
    remindToast,
    restoreToast,
    wikiErrorMessage,
} from './wikiPageCopy';

const enc = encodeURIComponent;

/**
 * The page view's right rail (spec B §1.4) — rendered in read AND edit mode.
 * The edit-mode cheat sheet is placed above these cards by WikiPage.
 */

/**
 * ACCESS — Wiki admins only (#871): opens the Access panel for this page's
 * own rules. Without any, the page uses its subcategory's or category's
 * access.
 */
export function PageAccessCard({ page }) {
    const [open, setOpen] = useState(false);
    const parentId = page.subcategory_id || page.category_id || null;
    const parentName = page.subcategory_name || page.category_name || '';
    return (
        <section className="wk-card wk-pg-access-card" aria-labelledby="wk-pg-access-label" data-testid="wiki-page-access">
            <h2 id="wk-pg-access-label" className="wk-t-label wk-pg-rail-label">{PAGE_COPY.accessLabel}</h2>
            <p className="wk-t-meta wk-pg-access-body">{PAGE_COPY.accessBody}</p>
            <button
                type="button"
                className="wk-btn wk-btn--secondary wk-btn--sm"
                data-testid="wiki-page-access-open"
                data-action="wiki.page_access"
                onClick={() => setOpen(true)}
            >
                <WikiIcon name="lock" size={14} />
                {PAGE_COPY.accessButton}
            </button>
            {open && (
                <WikiAccessPanel
                    scope="page"
                    scopeId={page.page_id}
                    name={page.title}
                    parentId={parentId}
                    parentName={parentName}
                    onClose={() => setOpen(false)}
                />
            )}
        </section>
    );
}

/**
 * ON THIS PAGE — one row per `#`/`##` heading of the SAVED page. Rows scroll
 * to the heading's anchor in read mode (prototype rows were inert; FE-markdown
 * gives every heading an id). In edit mode the read view isn't mounted, so the
 * rows are plain text, like the prototype.
 */
export function PageTocCard({ headings, interactive = true }) {
    const scrollTo = (id) => {
        const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    return (
        <section className="wk-card wk-pg-toc-card" aria-labelledby="wk-pg-toc-label" data-testid="wiki-page-toc">
            <h2 id="wk-pg-toc-label" className="wk-t-label wk-pg-rail-label">{PAGE_COPY.tocLabel}</h2>
            {headings.length === 0 ? (
                <p className="wk-t-meta wk-pg-toc-empty">{PAGE_COPY.tocEmpty}</p>
            ) : (
                <ul className="wk-pg-toc">
                    {headings.map((heading) => (
                        <li key={heading.id}>
                            {interactive ? (
                                <button type="button" className="wk-pg-toc-item" data-target={heading.id} onClick={() => scrollTo(heading.id)}>
                                    {heading.text}
                                </button>
                            ) : (
                                <span className="wk-pg-toc-item">{heading.text}</span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

/**
 * ACKNOWLEDGEMENTS — required published pages only. Progress bar + "{n} of
 * {m} people have acknowledged" + "Remind the rest" for the page owner and
 * admins (recommended gating, spec B §1.8) while anyone is still pending.
 */
export function PageAcksCard({ page, canRemind }) {
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const readBy = Number(page.read_by) || 0;
    const readOf = Number(page.read_of) || 0;
    const pending = Math.max(0, readOf - readBy);
    const percent = readOf > 0 ? Math.min(100, Math.round((100 * readBy) / readOf)) : 0;
    const remind = useMutation({
        mutationFn: () => client.post(`/wiki/pages/${enc(page.page_id)}/remind`, {}),
        onSuccess: (res) => {
            const sent = typeof res?.sent === 'number' ? res.sent : pending;
            wikiUi.toast(remindToast(sent, res?.version || ackVersion(page)));
            if (res && typeof res.read_by === 'number') {
                queryClient.setQueryData(wikiKeys.page(page.page_id), (old) => (old
                    ? { ...old, read_by: res.read_by, read_of: res.read_of ?? old.read_of }
                    : old));
            }
            invalidateWiki(queryClient, ['page', page.page_id]);
        },
        onError: (error) => {
            wikiUi.toast(wikiErrorMessage(error, "Couldn't send the reminder. Please try again."));
        },
    });
    return (
        <section className="wk-card wk-pg-acks" aria-labelledby="wk-pg-acks-label" data-testid="wiki-page-acks">
            <h2 id="wk-pg-acks-label" className="wk-t-label wk-pg-rail-label">{PAGE_COPY.acksLabel}</h2>
            <div
                className="wk-pg-acks-track"
                role="progressbar"
                aria-label={PAGE_COPY.acksLabel}
                aria-valuemin={0}
                aria-valuemax={readOf}
                aria-valuenow={readBy}
            >
                <div className="wk-pg-acks-fill" style={{ width: `${percent}%` }} data-testid="wiki-page-acks-fill" />
            </div>
            <p className="wk-t-meta wk-pg-acks-text">{readLabel(readBy, readOf)}</p>
            {canRemind && pending > 0 && (
                <button
                    type="button"
                    className="wk-btn wk-btn--secondary wk-pg-remind"
                    data-testid="wiki-page-remind"
                    disabled={remind.isPending}
                    onClick={() => remind.mutate()}
                >
                    <WikiIcon name="bell" size={13} />
                    {PAGE_COPY.remind}
                </button>
            )}
        </section>
    );
}

/**
 * VERSIONS — newest first, four rows (prototype `wkVersions(...).slice(0,4)`):
 * `v{n}` + Current pill + date, "{note} · {who}". Older rows get Compare
 * (anyone who can read) and Restore (page editors) — restore writes the old
 * body into the DRAFT (v{n+1}), toasts, and opens the editor. Both links are
 * hidden while editing: the editor owns the draft then.
 */
export function PageVersionsCard({ page, canEdit, editing = false, onCompare }) {
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const pageId = page.page_id;
    const versionsQuery = useWikiQuery(
        [...wikiKeys.page(pageId), 'versions'],
        `/wiki/pages/${enc(pageId)}/versions?limit=4`,
    );
    const restore = useMutation({
        mutationFn: (v) => client.post(`/wiki/pages/${enc(pageId)}/versions/${v}/restore`, {
            base_version: Number(page.version) || 0,
        }),
        onSuccess: (res, v) => {
            if (res && res.page_id) queryClient.setQueryData(wikiKeys.page(pageId), res);
            const draftVersion = res?.draft_version || (Number(page.version) || 0) + 1;
            wikiUi.toast(restoreToast(v, draftVersion));
            invalidateWiki(queryClient, ['page', pageId], 'pages', 'home');
            navigate(wikiPaths.pageEdit(pageId));
        },
        onError: (error) => {
            wikiUi.toast(wikiErrorMessage(error, "Couldn't restore that version. Please try again."));
            if (wikiErrorStatus(error) === 409) invalidateWiki(queryClient, ['page', pageId]);
        },
    });

    const onRestore = (v) => {
        if (page.has_draft && typeof window !== 'undefined' && typeof window.confirm === 'function'
            && !window.confirm(PAGE_COPY.restoreOverDraftConfirm)) return;
        restore.mutate(v);
    };

    const versions = (versionsQuery.data && Array.isArray(versionsQuery.data.versions))
        ? versionsQuery.data.versions.slice(0, 4)
        : [];
    const currentVersion = Number(page.version) || 0;

    return (
        <section className="wk-card wk-pg-versions" aria-labelledby="wk-pg-versions-label" data-testid="wiki-page-versions">
            <div className="wk-pg-versions-head">
                <WikiIcon name="history" size={13} />
                <h2 id="wk-pg-versions-label" className="wk-t-label wk-pg-rail-label">{PAGE_COPY.versionsLabel}</h2>
            </div>
            {versionsQuery.isLoading && <p className="wk-t-meta wk-pg-rail-empty">Loading versions…</p>}
            {versionsQuery.isError && (
                <p className="wk-t-meta wk-pg-rail-empty">
                    Versions didn't load.{' '}
                    <button type="button" className="wk-link" onClick={() => versionsQuery.refetch()}>Try again</button>
                </p>
            )}
            {versionsQuery.isSuccess && versions.length === 0 && (
                <p className="wk-t-meta wk-pg-rail-empty">{PAGE_COPY.versionsEmpty}</p>
            )}
            {versions.length > 0 && (
                <ul className="wk-pg-vers">
                    {versions.map((row) => {
                        const v = Number(row.v) || 0;
                        const isCurrent = Boolean(row.current);
                        const who = row.author_name || '';
                        const note = row.note || '';
                        return (
                            <li key={row.version_id || v} className="wk-pg-ver" data-testid="wiki-page-version" data-version={v}>
                                <div className="wk-pg-ver-line">
                                    <span className="wk-pg-ver-v">{`v${v}`}</span>
                                    {isCurrent && <span className="wk-pill wk-pill--green">{PAGE_COPY.current}</span>}
                                    <span className="wk-t-meta wk-pg-ver-when">{dateLabel(row.created_at)}</span>
                                </div>
                                {(note || who) && (
                                    <p className="wk-t-meta wk-pg-ver-note">{[note, who].filter(Boolean).join(' · ')}</p>
                                )}
                                {!isCurrent && !editing && v !== currentVersion && (
                                    <div className="wk-pg-ver-links">
                                        {currentVersion > 0 && (
                                            <button type="button" className="wk-link" onClick={() => onCompare(v)}>
                                                {PAGE_COPY.compare}
                                            </button>
                                        )}
                                        {canEdit && (
                                            <button
                                                type="button"
                                                className="wk-link"
                                                disabled={restore.isPending}
                                                onClick={() => onRestore(v)}
                                            >
                                                {PAGE_COPY.restore}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
